import { useHistoryPage } from '../hooks/useHistoryPage';
import { HistoryControls } from './shared/HistoryControls';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Button, Form, Alert, Table, Badge } from 'react-bootstrap';
import { parsePhoneNumber, formatPhoneNumberForDisplay } from '../utils/phoneNumberParser';
import { isValidStateAbbreviation } from '../utils/stateValidator';
import editIcon from '../assets/icons/edit.svg';
import trashIcon from '../assets/icons/trash.svg';
import saveIcon from '../assets/icons/save.svg';
import closeIcon from '../assets/icons/close.svg';
import alertIcon from '../assets/icons/alert-triangle.svg';
import checkCircleIcon from '../assets/icons/check-circle.svg';
import { formatPaymentMethod } from '../utils/paymentMethodFormatter';
import { TransactionLink, VehicleLink, PaymentLink, RTOLink } from './EntityLinks';
import { CommunicationTimeline, InternalNoteForm } from './CommunicationTimeline';
import { StatusBadge } from './shared/StatusBadge';
import { useDetailView } from '../hooks/useDetailView';
import { DeleteConfirmModal } from './shared/DeleteConfirmModal';

/**
 * CustomerDetail component displays and allows editing of a specific customer
 * Also shows customer balance and account summary
 */
export function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { apiCall, user } = useOutletContext();
  const transactionHistory = useHistoryPage(apiCall, `/transactions?customer_id=${id}`);
  const paymentHistory = useHistoryPage(apiCall, `/payments?customer_id=${id}`);
  const rtoHistory = useHistoryPage(apiCall, `/rto-agreements?customer_id=${id}`);
  const customerTransactions = transactionHistory.data;
  const customerPayments = paymentHistory.data;
  const customerRTOs = rtoHistory.data;
  const [smsConsent, setSmsConsent] = useState(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [showConsentConfirm, setShowConsentConfirm] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const communicationRef = useRef(null);

  // Custom validation function that also normalizes phone number
  const validateAndNormalizeCustomer = async (data) => {
    try {
      const parsed = parsePhoneNumber(data.phone_number);
      // Normalize the phone number in formData
      setFormData(prev => ({ ...prev, phone_number: parsed }));
      data.phone_number = parsed;
    } catch (error) {
      return error.message;
    }

    if (!isValidStateAbbreviation(data.driver_license_state)) {
      return 'Please enter a valid US state abbreviation (2 characters)';
    }

    return null;
  };

  const handleSaveWithValidation = (e) => {
    handleSave(e, validateAndNormalizeCustomer);
  };

  // Use detail view hook
  const {
    data: customer,
    loading,
    isEditing,
    formData,
    setFormData,
    error,
    setError,
    successMessage,
    startEditing,
    cancelEditing,
    handleChange,
    handleSave,
    loadData,
  } = useDetailView({
    fetchData: (customerId) => apiCall(`${API_BASE_URL}/customers/${customerId}`),
    saveData: async (customerId, data) => {
      // Extract sms_consent before sending to customer update API
      const { sms_consent, ...customerData } = data;

      const response = await apiCall(`${API_BASE_URL}/customers/${customerId}`, {
        method: 'PUT',
        body: JSON.stringify(customerData),
      });

      // If consent was checked and not already active, record it
      if (sms_consent && !smsConsent) {
        try {
          await apiCall(`${API_BASE_URL}/customers/${customerId}/consent`, {
            method: 'POST',
            body: JSON.stringify({ consent_given: true }),
          });
          setSmsConsent(true);
        } catch (err) {
          console.error('Failed to record SMS consent:', err);
        }
      }

      return response;
    },
  });

  // Custom input handler for state abbreviation
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'driver_license_state') {
      e.target.value = value.slice(0, 2).toUpperCase();
    }
    handleChange(e);
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await apiCall(`${API_BASE_URL}/customers/${customer.id}`, { method: 'DELETE' });
      setShowDeleteModal(false);
      navigate('/dashboard/customers');
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Sync consent state from customer data
  useEffect(() => {
    if (customer?.sms_consent_active !== undefined) {
      setSmsConsent(customer.sms_consent_active);
    }
  }, [customer]);

  // Record SMS consent handler
  const handleRecordConsent = async () => {
    setConsentLoading(true);
    try {
      const response = await apiCall(`${API_BASE_URL}/customers/${id}/consent`, {
        method: 'POST',
        body: JSON.stringify({ consent_given: true }),
      });
      if (response.success) {
        setSmsConsent(true);
        setShowConsentConfirm(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to record consent');
    } finally {
      setConsentLoading(false);
    }
  };

  // Load customer and related data
  useEffect(() => {
    const customerId = parseInt(id);
    if (!customerId) return;

    loadData(customerId);

  }, [id, apiCall, loadData]);

  // Scroll to communication timeline if navigated from unread messages
  useEffect(() => {
    if (location.state?.focusSms && !loading && communicationRef.current) {
      communicationRef.current.scrollIntoView({ behavior: 'smooth' });
      // Clear the state so refresh doesn't re-scroll
      window.history.replaceState({}, '');
    }
  }, [location.state?.focusSms, loading]);

  const handleNoteAdded = useCallback(() => {
    setTimelineRefreshKey(prev => prev + 1);
  }, []);

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div>Loading customer details...</div>
      </Container>
    );
  }

  if (error && !customer) {
    return (
      <Container fluid className="py-4">
        <Button variant="link" onClick={() => navigate('/dashboard/customers')} className="p-0">
          ← Back to Customers
        </Button>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }


  // Calculate payment status for a transaction
  const getTransactionPaymentStatus = (transaction) => {
    const totalPaid = Number(transaction.total_paid || 0);
    const transactionAmount = parseFloat(transaction.amount) + parseFloat(transaction.tax_amount || 0);

    if (totalPaid >= transactionAmount) {
      return { status: 'Paid', variant: 'success', icon: checkCircleIcon };
    } else if (totalPaid > 0) {
      return { status: 'Partial', variant: 'warning', icon: alertIcon };
    } else {
      return { status: 'Unpaid', variant: 'danger', icon: closeIcon };
    }
  };

  return (
    <Container fluid className="py-4 domain-customers">
      <Row className="mb-3">
        <Col>
          <Button variant="link" onClick={() => navigate('/dashboard/customers')} className="p-0">
            ← Back to Customers
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h2>
            {customer.first_name} {customer.last_name}
            <span style={{ fontSize: '0.8em', marginLeft: '10px', color: '#666' }}>
              (ID: {customer.id})
            </span>
          </h2>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      {!isEditing ? (
        <>
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h3>Customer Information</h3>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>First Name</strong>
                      <span>{customer.first_name}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Last Name</strong>
                      <span>{customer.last_name}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Email</strong>
                      <span>{customer.email || '-'}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Phone</strong>
                      <span>{formatPhoneNumberForDisplay(customer.phone_number)}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>License Number</strong>
                      <span>{customer.driver_license_number}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>License State</strong>
                      <span>{customer.driver_license_state}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>International License</strong>
                      <span>{customer.international_license ? 'Yes' : 'No'}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Referral</strong>
                      <span>{customer.referral || '-'}</span>
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <h5 className="mb-3">Address Information</h5>
                  <Row>
                    <Col md={12} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Street Address</strong>
                      <span>{customer.address || '-'}</span>
                    </Col>
                    <Col md={4} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>City</strong>
                      <span>{customer.city || '-'}</span>
                    </Col>
                    <Col md={4} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>State</strong>
                      <span>{customer.state || '-'}</span>
                    </Col>
                    <Col md={4} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>ZIP Code</strong>
                      <span>{customer.zip_code || '-'}</span>
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <h5 className="mb-3">Insurance Information</h5>
                  <Row>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Insurance Company</strong>
                      <span>{customer.insurance_company || '-'}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Policy Number</strong>
                      <span>{customer.insurance_policy_number || '-'}</span>
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <h5 className="mb-3">SMS Consent</h5>
                  <Row>
                    <Col md={12} className="mb-3">
                      {smsConsent === true ? (
                        <Badge bg="success">SMS Consent: Active</Badge>
                      ) : smsConsent === false ? (
                        <>
                          <Badge bg="secondary">SMS Consent: None</Badge>
                          {user?.role !== 'auditor' && !showConsentConfirm && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="ms-2"
                              onClick={() => setShowConsentConfirm(true)}
                            >
                              Record SMS Consent
                            </Button>
                          )}
                          {showConsentConfirm && (
                            <Alert variant="info" className="mt-2">
                              <p className="mb-2"><strong>Confirm:</strong> Customer has verbally or in writing agreed to receive SMS notifications from Tegrat Auto. Message frequency varies. Message and data rates may apply.</p>
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                onClick={handleRecordConsent}
                                disabled={consentLoading}
                              >
                                {consentLoading ? 'Recording...' : 'Confirm Consent'}
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowConsentConfirm(false)}
                              >
                                Cancel
                              </Button>
                            </Alert>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">Loading...</span>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              {customer.wallet && (
                <Card className="mb-3">
                  <Card.Header>
                    <h3>Wallet Information</h3>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6} className="mb-3">
                        <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Total Billed</strong>
                        <span>${customer.wallet.total_billed?.toFixed(2) || '0.00'}</span>
                      </Col>
                      <Col md={6} className="mb-3">
                        <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Total Paid</strong>
                        <span>${customer.wallet.total_paid?.toFixed(2) || '0.00'}</span>
                      </Col>
                      <Col md={6} className="mb-3">
                        <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Current Balance</strong>
                        <span style={{
                          color: customer.wallet.current_balance > 0 ? '#d32f2f' : '#388e3c',
                          fontWeight: 'bold',
                        }}>
                          ${Math.abs(customer.wallet.current_balance).toFixed(2)}
                        </span>
                      </Col>
                      <Col md={6} className="mb-3">
                        <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Status</strong>
                        <span style={{
                          color: customer.wallet.status === 'current' ? '#388e3c' :
                                customer.wallet.status === 'past_due' ? '#d32f2f' : '#666',
                          fontWeight: customer.wallet.status === 'past_due' ? 'bold' : 'normal',
                        }}>
                          {customer.wallet.status === 'current' ? 'Current' :
                           customer.wallet.status === 'past_due' ? 'Past Due' :
                           customer.wallet.status === 'closed' ? 'Closed' :
                           customer.wallet.status || '-'}
                        </span>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
              {user?.role !== 'auditor' && (
                <InternalNoteForm
                  customerId={parseInt(id)}
                  apiCall={apiCall}
                  onNoteAdded={handleNoteAdded}
                />
              )}
            </Col>
          </Row>
        </>
      ) : (
        <Card className="mb-4">
          <Card.Header>
            <h3>Edit Customer</h3>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSaveWithValidation}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Driver License Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="driver_license_number"
                      value={formData.driver_license_number}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Driver License State</Form.Label>
                    <Form.Control
                      type="text"
                      name="driver_license_state"
                      value={formData.driver_license_state}
                      onChange={handleFormChange}
                      required
                      maxLength={2}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>International License</Form.Label>
                    <Form.Select
                      name="international_license"
                      value={formData.international_license ? 'yes' : 'no'}
                      onChange={handleFormChange}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Referral</Form.Label>
                    <Form.Control
                      type="text"
                      name="referral"
                      value={formData.referral || ''}
                      onChange={handleFormChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr className="my-3" />
              <h5 className="mb-3">Address Information (Optional)</h5>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Street Address</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={formData.address || ''}
                      onChange={handleFormChange}
                      placeholder="123 Main Street, Apt 4B"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={5}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={formData.city || ''}
                      onChange={handleFormChange}
                      placeholder="Seattle"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>State</Form.Label>
                    <Form.Control
                      type="text"
                      name="state"
                      value={formData.state || ''}
                      onChange={handleFormChange}
                      placeholder="WA"
                      maxLength={2}
                      style={{ textTransform: 'uppercase' }}
                    />
                    <Form.Text className="text-muted">
                      2-letter state code
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="zip_code"
                      value={formData.zip_code || ''}
                      onChange={handleFormChange}
                      placeholder="98101"
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr className="my-3" />
              <h5 className="mb-3">Insurance Information (Optional)</h5>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Insurance Company</Form.Label>
                    <Form.Control
                      type="text"
                      name="insurance_company"
                      value={formData.insurance_company || ''}
                      onChange={handleFormChange}
                      placeholder="State Farm, Geico, etc."
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Policy Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="insurance_policy_number"
                      value={formData.insurance_policy_number || ''}
                      onChange={handleFormChange}
                      placeholder="Policy number"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {!smsConsent && (
                <>
                  <hr className="my-3" />
                  <h5 className="mb-3">SMS Consent</h5>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="sms_consent_edit"
                      name="sms_consent"
                      label="Customer consents to receive SMS notifications"
                      checked={formData.sms_consent || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, sms_consent: e.target.checked }))}
                    />
                    <Form.Text className="text-muted">
                      Message frequency varies. Message and data rates may apply.
                    </Form.Text>
                  </Form.Group>
                </>
              )}

              <div className="mt-3">
                <Button type="submit" variant="primary" className="me-2">
                  <img src={saveIcon} alt="" className="icon-btn" />
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={cancelEditing}
                >
                  <img src={closeIcon} alt="" className="icon-btn" />
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-4">
        <Card.Header>
          <h3>Account Summary</h3>
        </Card.Header>
        <Card.Body>
          <h5 className="mb-3">Transactions ({transactionHistory.total || 0})</h5>
            <HistoryControls history={transactionHistory} label="transactions" />
            {customerTransactions.length > 0 ? (
              <Table striped hover responsive className="mb-4">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Vehicle</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Amount on this page</th>
                    <th>Payment Status</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {customerTransactions.map(t => {
                    // Get vehicle info
                    const vehicleDisplay = (t.make && t.model)
                      ? `${t.year || ''} ${t.make} ${t.model}`.trim()
                      : (t.vehicle_id ? `Vehicle #${t.vehicle_id}` : '-');

                    // Get payment status
                    const paymentStatus = getTransactionPaymentStatus(t);

                    return (
                      <tr key={t.id}>
                        <td>
                          <TransactionLink transactionId={t.id}>
                            #{t.id}
                          </TransactionLink>
                        </td>
                        <td>{t.type.toUpperCase()}</td>
                        <td>
                          {t.vehicle_id ? (
                            <VehicleLink
                              vehicleId={t.vehicle_id}
                              vehicleInfo={vehicleDisplay}
                            />
                          ) : '-'}
                        </td>
                        <td>{t.start_date?.split('T')[0] || '-'}</td>
                        <td>{t.end_date?.split('T')[0] || '-'}</td>
                        <td>${(parseFloat(t.amount) + parseFloat(t.tax_amount || 0)).toFixed(2)}</td>
                        <td>
                          <Badge bg={paymentStatus.variant}>
                            <img src={paymentStatus.icon} alt="" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />
                            {paymentStatus.status}
                          </Badge>
                        </td>
                        <td>{t.notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="text-muted text-center py-3">No transactions found</div>
            )}

            <h5 className="mb-3 mt-4">Payments ({paymentHistory.total || 0})</h5>
            <HistoryControls history={paymentHistory} label="payments" />
            {customerPayments.length > 0 ? (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Payment(s)</th>
                    <th>Date</th>
                    <th>Total Amount</th>
                    <th>Payment Method</th>
                    <th>Applied to Transaction(s)</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Group payments by date
                    const paymentsByDate = customerPayments.reduce((acc, payment) => {
                      const date = payment.payment_date ? payment.payment_date.split('T')[0] : 'Unknown';
                      if (!acc[date]) {
                        acc[date] = {
                          date: date,
                          total: 0,
                          count: 0,
                          payments: [],
                          transactionIds: new Set()
                        };
                      }
                      acc[date].total += parseFloat(payment.amount) || 0;
                      acc[date].count += 1;
                      acc[date].payments.push(payment);
                      // Track unique transaction IDs
                      if (payment.transaction_id) {
                        acc[date].transactionIds.add(payment.transaction_id);
                      }
                      return acc;
                    }, {});

                    // Convert to array and sort by date (newest first)
                    const sortedDates = Object.values(paymentsByDate).sort((a, b) => 
                      new Date(b.date) - new Date(a.date)
                    );

                    return sortedDates.map((dateGroup, idx) => {
                      // Format transaction IDs and RTO IDs display
                      let transactionDisplay;

                      // Collect RTO IDs from payments
                      const rtoIds = new Set();
                      dateGroup.payments.forEach(payment => {
                        if (payment.rto_agreement_id) {
                          rtoIds.add(payment.rto_agreement_id);
                        }
                      });

                      const hasTransactions = dateGroup.transactionIds.size > 0;
                      const hasRTOs = rtoIds.size > 0;

                      if (!hasTransactions && !hasRTOs) {
                        transactionDisplay = <span style={{ color: '#dc3545' }}><img src={alertIcon} alt="" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />No transaction linked</span>;
                      } else {
                        const displayItems = [];

                        // Add transaction links
                        if (hasTransactions) {
                          const transactionIdArray = Array.from(dateGroup.transactionIds).sort((a, b) => a - b);
                          transactionIdArray.forEach(id => {
                            displayItems.push(
                              <TransactionLink key={`trans-${id}`} transactionId={id}>#{id}</TransactionLink>
                            );
                          });
                        }

                        // Add RTO links
                        if (hasRTOs) {
                          const rtoIdArray = Array.from(rtoIds).sort((a, b) => a - b);
                          rtoIdArray.forEach(id => {
                            displayItems.push(
                              <RTOLink key={`rto-${id}`} rtoId={id}>RTO #{id}</RTOLink>
                            );
                          });
                        }

                        // Render with commas
                        transactionDisplay = displayItems.map((item, index) => (
                          <span key={item.key}>
                            {item}
                            {index < displayItems.length - 1 && ', '}
                          </span>
                        ));
                      }

                      // Get unique payment methods for this date
                      const paymentMethods = [...new Set(dateGroup.payments.map(p => p.payment_method || 'unknown'))];
                      const paymentMethodDisplay = paymentMethods.map(m => formatPaymentMethod(m)).join(', ');

                      return (
                        <tr key={idx}>
                          <td>
                            {dateGroup.payments
                              .slice()
                              .sort((a, b) => a.id - b.id)
                              .map((payment, paymentIndex) => (
                                <React.Fragment key={payment.id}>
                                  <PaymentLink paymentId={payment.id}>#{payment.id}</PaymentLink>
                                  {paymentIndex < dateGroup.payments.length - 1 && ', '}
                                </React.Fragment>
                              ))}
                          </td>
                          <td>{dateGroup.date}</td>
                          <td>${dateGroup.total.toFixed(2)}</td>
                          <td>{paymentMethodDisplay}</td>
                          <td>{transactionDisplay}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </Table>
            ) : (
              <div className="text-muted text-center py-3">No payments found</div>
            )}

            {/* RTO Agreements Section */}
            {(customerRTOs.length > 0 || rtoHistory.error) && (
              <>
                <h5 className="mb-3 mt-4">RTO Agreements ({rtoHistory.total || 0})</h5>
                <HistoryControls history={rtoHistory} label="leases" />
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Vehicle</th>
                      <th>Sale Price</th>
                      <th>Balance</th>
                      <th>Next Due</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerRTOs.map(rto => (
                      <tr key={rto.id}>
                        <td>
                          <RTOLink rtoId={rto.id}>
                            #{rto.id}
                          </RTOLink>
                        </td>
                        <td>
                          <VehicleLink
                            vehicleId={rto.vehicle_id}
                            vehicleInfo={`${rto.vehicle_year || ''} ${rto.vehicle_make || ''} ${rto.vehicle_model || ''}`}
                          >
                            {rto.vehicle_make} {rto.vehicle_model}
                          </VehicleLink>
                          <br />
                          <small className="text-muted">{rto.vehicle_license_plate}</small>
                        </td>
                        <td>${parseFloat(rto.total_sale_price).toLocaleString()}</td>
                        <td style={{ color: parseFloat(rto.balance_remaining) > 0 ? '#d32f2f' : '#388e3c', fontWeight: 'bold' }}>
                          ${parseFloat(rto.balance_remaining).toLocaleString()}
                        </td>
                        <td>{rto.next_payment_due_date?.split('T')[0] || '-'}</td>
                        <td>
                          <StatusBadge status={rto.status} type="rto" delinquencyStatus={rto.delinquency_status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Card.Body>
        </Card>

      {/* Communication Timeline */}
      <div ref={communicationRef}>
        <CommunicationTimeline customerId={parseInt(id)} apiCall={apiCall} refreshKey={timelineRefreshKey} />
      </div>

      {!isEditing && user?.role !== 'auditor' && (
        <Row className="mb-4 mt-4">
          <Col>
            <Button
              variant="primary"
              onClick={() => startEditing()}
              className="me-2"
            >
              <img src={editIcon} alt="" className="icon-btn" />
              Edit Customer
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
            >
              <img src={trashIcon} alt="" className="icon-btn" />
              Delete Customer
            </Button>
          </Col>
        </Row>
      )}
      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        itemLabel="customer"
        error={deleteError}
        isDeleting={isDeleting}
      />
    </Container>
  );
}

export default CustomerDetail;
